import Router from 'next/router';
import { useTheme } from 'next-themes';
import { memo, useMemo } from 'react';

import { useAnalyticsDebug } from '@air/analytics';
import { ActivityIcon, CheckIcon, PaletteIcon, WandIcon } from '@air/design-icons';
import { Avatar } from '@air/primitive-avatar';
import { Badge } from '@air/primitive-badge';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';

import { AccountButtonSkeleton } from '~/components/Layouts/HomeLayout/SideNav/AccountButtonSkeleton';
import { Routes } from '~/constants/routes';
import { ACCOUNT_POPOVER_BUTTON, ACCOUNT_SETTINGS_LINK } from '~/constants/testIDs';
import { useDevTools } from '~/hooks/useDevTools';
import { useAccountContext } from '~/providers/AccountProvider';
import { useLogout } from '~/swr-hooks/auth/useLogout';
import { getAvatarImageSrc } from '~/utils/getAvatarImageSrc';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';

const AVATAR_LARGE_SIZE = 32;

export const AccountButton = memo(() => {
  const { data: account } = useAccountContext();
  const { logout } = useLogout();
  const { theme, setTheme } = useTheme();
  const { isDevToolsEnabled } = useDevTools();
  const { value, onToggle } = useAnalyticsDebug();

  const menuOptions: DropdownMenuOption[] = useMemo(() => {
    const optionsArray: DropdownMenuOption[] = [];

    optionsArray.push(
      {
        id: 'account-settings-separator',
        type: 'separator',
      },
      {
        ...getDropdownMenuOption('account-settings'),
        id: ACCOUNT_SETTINGS_LINK,
        onSelect: () => Router.push(Routes.account.myAccount),
        type: 'item',
      },
      {
        id: 'download-separator',
        type: 'separator',
      },
      {
        ...getDropdownMenuOption('download-air-flow'),
        onSelect: () => window.open(Routes.marketing.desktop, '_blank'),
        type: 'item',
      },
    );

    if (isDevToolsEnabled) {
      optionsArray.push(
        {
          id: 'developer-tools',
          label: 'Developer Tools',
          prefix: <WandIcon size="sm" />,
          options: [
            {
              id: 'analytics-debugger',
              label: 'Analytics Debugger',
              onSelect: onToggle,
              prefix: <ActivityIcon size="sm" />,
              suffix: value ? <CheckIcon size="sm" /> : undefined,
              type: 'item',
            },
          ],
          type: 'sub',
        },
        {
          id: 'devtools-separator',
          type: 'separator',
        },
      );
    }

    optionsArray.push({
      id: 'switch-theme',
      label: 'Switch theme',
      prefix: <PaletteIcon size="sm" />,
      suffix: <Badge color="teal">Beta</Badge>,
      options: [
        {
          id: 'theme',
          onValueChange: (value) => setTheme(value),
          options: [
            {
              id: 'system',
              label: 'System',
              value: 'system',
              type: 'radio-item',
            },
            {
              id: 'light',
              label: 'Light',
              value: 'light',
              type: 'radio-item',
            },
            {
              id: 'dark',
              label: 'Dark',
              value: 'dark',
              type: 'radio-item',
            },
          ],
          type: 'radio-group',
          value: theme,
        },
      ],
      type: 'sub',
    });

    optionsArray.push({
      ...getDropdownMenuOption('log-out'),
      onSelect: logout,
      type: 'item',
    });

    return optionsArray;
  }, [isDevToolsEnabled, logout, onToggle, setTheme, theme, value]);

  if (!account) {
    return <AccountButtonSkeleton />;
  }

  const { avatar, firstName, lastName, email, id: userId } = account;

  return (
    <div className="relative px-1.5">
      <DropdownMenu
        className="w-[296px]"
        size="small"
        trigger={
          <button
            data-testid={ACCOUNT_POPOVER_BUTTON}
            className="flex size-10 cursor-pointer items-center justify-center rounded-4 border-none bg-transparent px-0"
            type="button"
          >
            <Avatar
              alt={`${firstName} ${lastName} avatar`}
              size={24}
              src={getAvatarImageSrc(avatar, 24)}
              style={{ cursor: 'pointer' }}
              text={firstName && lastName ? `${firstName[0]}${lastName[0]}` : ''}
              colorSeed={userId}
            />
          </button>
        }
      >
        <header className="mb-2 flex items-center py-1">
          <Avatar
            alt={`${firstName} ${lastName} avatar`}
            size={AVATAR_LARGE_SIZE}
            src={getAvatarImageSrc(avatar, AVATAR_LARGE_SIZE)}
            text={firstName && lastName ? `${firstName[0]}${lastName[0]}` : ''}
            className="mr-3 shrink-0"
            colorSeed={userId}
          />
          <div>
            <div className="mb-0.5 truncate font-semibold ads:text-secondary text-16 text-gray-11">
              {firstName} {lastName}
            </div>
            <div className="truncate ads:text-metadata text-12 text-gray-9">{email}</div>
          </div>
        </header>
        {renderDropdownItems({ options: menuOptions })}
      </DropdownMenu>
    </div>
  );
});

AccountButton.displayName = 'AccountButton';
